// ProfileDriftCard — surfaces gentle "profile freshness" signals so the
// therapist can keep the child's language map, weekly focus, and profile
// fields in sync with reality. Silent when the backend returns 0 signals.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertCircle, Info, X, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ACTION_TO_HASH = {
  interests: 'edit',
  profile: 'edit',
  regulation_supports: 'edit',
  strengths_notes: 'edit',
  glp_stage: 'edit',
  weekly_focus: 'weekly-focus',
  progress: 'progress',
  assessments: 'assessments',
};

export default function ProfileDriftCard({ childId, onAction }) {
  const [signals, setSignals] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/children/${childId}/profile-drift`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setSignals(res.data?.signals || []);
      } catch (e) {
        if (!cancelled) setSignals([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (childId) load();
    return () => { cancelled = true; };
  }, [childId]);

  if (loading || dismissed || signals.length === 0) return null;

  // Highest severity first
  const sorted = [...signals].sort((a, b) => {
    const w = { warn: 0, info: 1 };
    return (w[a.severity] ?? 2) - (w[b.severity] ?? 2);
  });

  const hasWarn = sorted.some((s) => s.severity === 'warn');

  return (
    <div
      className={`mb-6 rounded-3xl border-2 overflow-hidden ${
        hasWarn ? 'bg-amber-50/60 border-amber-200' : 'bg-blue-50/50 border-blue-200'
      }`}
      data-testid="profile-drift-card"
    >
      <div className="p-4 sm:p-5">
        <div className="flex items-start justify-between mb-3 gap-3">
          <div className="flex items-center gap-2">
            <div className={`w-9 h-9 rounded-2xl flex items-center justify-center ${
              hasWarn ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
            }`}>
              <Sparkles className="w-4 h-4" />
            </div>
            <div>
              <p className={`text-[10px] uppercase tracking-widest font-semibold ${
                hasWarn ? 'text-amber-700' : 'text-blue-700'
              }`}>
                Profile freshness
              </p>
              <p className="font-serif text-lg text-stone-800">
                {sorted.length === 1
                  ? '1 gentle nudge'
                  : `${sorted.length} gentle nudges`}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="p-1 rounded-full hover:bg-white/60 text-stone-500 flex-shrink-0"
            aria-label="Dismiss for this session"
            data-testid="profile-drift-dismiss"
            title="Dismiss for this session"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <ul className="space-y-2">
          {sorted.map((s) => (
            <li
              key={s.kind}
              className={`bg-white rounded-2xl p-3 border ${
                s.severity === 'warn' ? 'border-amber-200' : 'border-blue-100'
              } flex items-start gap-3`}
              data-testid={`profile-drift-signal-${s.kind}`}
            >
              <div className="flex-shrink-0 pt-0.5">
                {s.severity === 'warn' ? (
                  <AlertCircle className="w-4 h-4 text-amber-600" />
                ) : (
                  <Info className="w-4 h-4 text-blue-600" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-stone-700 leading-relaxed">{s.message}</p>
              </div>
              {s.action_label && (
                <button
                  type="button"
                  onClick={() => onAction?.(ACTION_TO_HASH[s.action_field] || 'edit', s)}
                  className={`text-xs font-medium px-3 py-1.5 rounded-full whitespace-nowrap ${
                    s.severity === 'warn'
                      ? 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                      : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                  }`}
                  data-testid={`profile-drift-action-${s.kind}`}
                >
                  {s.action_label}
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
